import {
    ALeadData,
    BaloiseBrokerInfo,
    BaloiseLeadInfo,
    BaloiseMetadata,
    BaloisePostQuoteRequest,
    BaloisePostQuoteRequestLeadType,
    BaloisePostWeTakeCareLeadRequest,
    BaloiseProductInfo,
    BaloiseVehicleInfo,
    BaloiseWtcReason,
    GuaranteeDto,
    SimulationDto,
} from "@lg-cfa/services";
import { getCityName } from "@lg-cfa/utils";
import { differenceInYears } from "date-fns";

import { sanitizeCurrencyInput } from "../price";

import {
    GetTariffParameterObjectCarType,
    InsuranceType,
    VehicleType,
} from "@enums";
import { Insurance, Tariff, UserDataInterface } from "@types";

const generateLeadInfo = (userData: UserDataInterface): BaloiseLeadInfo => {
    return {
        firstName: userData.yourInformationFirstName,
        lastName: userData.yourInformationLastName,
        email: userData.yourInformationEmail,
        telephoneNumber: userData.yourInformationPhone,
        extraInfo: userData.yourInformationQuestion,
        consentGiven: userData.yourInformationConsent,
    };
};
const makeVehicleInfoObject = (
    userData: UserDataInterface,
): BaloiseVehicleInfo => {
    const carValue =
        userData.carType === GetTariffParameterObjectCarType.NewCar
            ? sanitizeCurrencyInput(userData.carDescriptionCatalogValue)
            : sanitizeCurrencyInput(userData.carDescriptionInvoiceValue);
    return {
        model: userData.carDescriptionModel,
        fuelType: userData.carDescriptionFuel,
        kwCount: Number(userData.carDescriptionPower),
        carValue: carValue,
        safetyFeaturesCount: userData.carDetailsSecurityOptions.length,
        vehicleType: VehicleType.PersonalCar,
        make: userData.carDescriptionMake,
        vehicleAgeType: userData.carType,
        yearlyDistance: Number(userData.carDetailsMileage),
        purchaseDate: userData.carDescriptionDatePurchased as unknown as Date,
        firstUseDate:
            userData.carDescriptionDateRegistration as unknown as Date,
    };
};

const generateLeadData = (
    userData: UserDataInterface,
    additionalCover: GuaranteeDto[],
    weTakeCareActivated: boolean,
    insuranceChoice?: Insurance,
): ALeadData => {
    return {
        insuranceType: insuranceChoice?.key,
        driverInfo: {
            birthDate: userData.driverInformationBirthday,
            address: {
                city: getCityName(userData.driverInformationCity),
                postalCode: userData.driverInformationPostalCode,
                street: userData.driverInformationStreet?.name,
                streetId: userData.driverInformationStreet?.id,
                houseNumber: userData.driverInformationNumber,
                bus: userData.driverInformationLetterBox,
            },
            driversLicenseStartDate: userData.driverHistory_License,
            accidentCount: Number(userData.driverHistory_Accidents),
            driverLast5Years:
                userData.driverHistory_Insurance === "0" ? false : true,
        },
        vehicleInfo: makeVehicleInfoObject(userData),
        guarantees: makeGuaranteeObject(
            additionalCover,
            weTakeCareActivated,
            insuranceChoice,
        ),
    };
};

const generateBrokerInfo = (userData: UserDataInterface): BaloiseBrokerInfo => {
    return {
        brokerId: userData.brokerData?.brokerId,
    };
};

const generateMetaData = (language: string): BaloiseMetadata => {
    return {
        language: language === "nl-BE" ? "NLD" : "FRA",
    };
};

const generateProductInfo = (
    carType?: GetTariffParameterObjectCarType,
): BaloiseProductInfo | undefined => {
    if (!carType) {
        return;
    }
    return {
        productType: "Car",
        productSubType: carType.replace("Car", ""), // New, Current, SecondHand
    };
};

const makeGuaranteeObject = (
    additionalCover: GuaranteeDto[],
    weTakeCareActivated: boolean,
    insuranceChoice?: Insurance,
): GuaranteeDto[] => {
    const result: GuaranteeDto[] = [];
    if (!weTakeCareActivated) {
        insuranceChoice?.guarantees.base.forEach((element) => {
            result.push(element);
        });

        additionalCover.forEach((element) => {
            result.push(element);
        });
    }
    return result;
};

export const generateCarLeadObject = (
    userData: UserDataInterface,
    additionalCover: GuaranteeDto[],
    weTakeCareActivated: boolean,
    language: string,
    insuranceChoice?: Insurance,
): BaloisePostQuoteRequest => {
    return {
        leadInfo: generateLeadInfo(userData),
        leadData: generateLeadData(
            userData,
            additionalCover,
            weTakeCareActivated,
            insuranceChoice,
        ),
        leadType: weTakeCareActivated
            ? BaloisePostQuoteRequestLeadType.WtcMin
            : BaloisePostQuoteRequestLeadType.QuoteLead,
        brokerInfo: generateBrokerInfo(userData),
        metadata: generateMetaData(language),
        productInfo: generateProductInfo(userData.carType),
    };
};

export const generateWTCCarLeadObject = (
    userData: UserDataInterface,
    additionalCover: GuaranteeDto[],
    weTakeCareActivated: boolean,
    language: string,
    insuranceChoice?: Insurance,
    wtcReason?: BaloiseWtcReason,
) => {
    const result: BaloisePostWeTakeCareLeadRequest = {
        leadRequestBody: {
            leadInfo: generateLeadInfo(userData),
            leadData: generateLeadData(
                userData,
                additionalCover,
                weTakeCareActivated,
                insuranceChoice,
            ),
            leadType: weTakeCareActivated
                ? BaloisePostQuoteRequestLeadType.WtcMin
                : BaloisePostQuoteRequestLeadType.QuoteLead,
            brokerInfo: generateBrokerInfo(userData),
            metadata: generateMetaData(language),
            productInfo: generateProductInfo(userData.carType),
        },
        wtcReason: wtcReason,
    };
    return result;
};

export const generateTariffObject = (
    simulationDto: SimulationDto,
    registrationDate: string,
    carType?: GetTariffParameterObjectCarType,
) => {
    const calculatePrice = (base?: GuaranteeDto[]) => {
        if (!base) {
            return 0;
        }
        return base.reduce((total, { price }) => total + (price ?? 0), 0);
    };

    const insurances = simulationDto.tariffs
        ? simulationDto.tariffs.map((tar): Insurance => {
              const description =
                  (InsuranceType as any)[tar.insuranceType as string] ===
                      "large.omnium" &&
                  (carType === GetTariffParameterObjectCarType.NewCar ||
                      differenceInYears(
                          new Date(),
                          new Date(registrationDate),
                      ) < 2)
                      ? "insurancechoice.choice.large.omnium.description.new"
                      : `insurancechoice.choice.${
                            (InsuranceType as any)[tar.insuranceType as string]
                        }.description`;

              return {
                  key: tar.insuranceType ?? "",
                  name: `insurancechoice.choice.${
                      (InsuranceType as any)[tar.insuranceType as string]
                  }.name`,
                  description,
                  wantToKnowMore: [
                      `insurancechoice.choice.${
                          (InsuranceType as any)[tar.insuranceType as string]
                      }.explanation`,
                  ],
                  calculatedPrice: calculatePrice(tar.guarantees?.base),
                  //TODO: guarantees type should be better and more accurate in ts client
                  guarantees: tar.guarantees as any,
                  badge:
                      tar.insuranceType === "BigOmnium"
                          ? "insurancechoice.mostchosen"
                          : "",
              };
          })
        : [];

    const tariffObject: Tariff = {
        insurances: insurances,
        deductible: simulationDto.deductible,
        highFiveBonus: simulationDto.highFiveBonus,
    };

    return tariffObject;
};
